// public/v3/src/lib/tabs.js — shared top-level v3 tab definitions.

export const TABS = Object.freeze([
  { v: 'timelogs',  full: 'Time Logs',     short: 'Logs' },
  { v: 'studio',    full: 'Review & Push', short: 'Review' },
  { v: 'apcm',      full: 'APCM Fallback', short: 'APCM' },
  { v: 'onboard',   full: 'Onboard',       short: 'Onboard' },
  { v: 'faxinbox',  full: 'Fax Inbox',     short: 'Faxes' },
  { v: 'orderchat', full: 'Order Chat',    short: 'Chat' },
  { v: 'voicelog',  full: 'Voice Log',     short: 'Voice' },
  { v: 'noteexpander', full: 'Note Expander', short: 'Expand' },
  // Provider letters (template builder + AI draft → preview → send). Shares the
  // orderchat capability (see TAB_FEATURE_ALIAS) — no separate catalog key.
  // `role` mirrors requireRole('admin','provider') on /api/provider-letters
  // (server/app.js:444): a physician-issued outbound action, not a staff one.
  { v: 'letters',   full: 'Letters',       short: 'Letters',   role: 'provider' },
  // Historic id: the tab began life as the Quest homebound form and grew into
  // the 3-mode Lab Orders surface — the id stays for deep links/prefs.
  // `role` mirrors requireRole('admin','provider') on /api/quest-homebound
  // (server/app.js:429) and /api/lab-orders (:438).
  { v: 'questhomebound', full: 'Lab Orders', short: 'Labs',    role: 'provider' },
  { v: 'intake',    full: 'Intake Review', short: 'Intake' },
  // Patient self-scheduling — the provider-facing hours / visit types / rules
  // page (pages/scheduling.jsx). `role` mirrors requireRole('admin','provider')
  // on /api/scheduling (server/app.js): admins and providers edit the
  // calendar, front-office staff do not. Its OWN catalog key ('scheduling'),
  // deliberately NOT a TAB_FEATURE_ALIAS entry: an alias to `intake` would put
  // this tab beside Intake Review for a provider-tier role whose only grant is
  // intake, and shouldAutoRouteToZephyr would then answer false for exactly the
  // person R7 routes to Zephyr (tests/unit/v3/tabs-auto-route.test.js:46).
  { v: 'scheduling', full: 'Scheduling', short: 'Schedule', role: 'provider' },
  { v: 'kiosk',              full: 'Kiosk',              short: 'Kiosk',      role: 'admin' },
  { v: 'usermgmt',           full: 'Users',              short: 'Users',      role: 'admin' },
  { v: 'bhi-assessment-admin', full: 'BHI Kiosk Admin', short: 'BHI Kiosk',  role: 'admin' },
]);

export const DEFAULT_TAB_ORDER = Object.freeze(TABS.map(t => t.v));

// Tabs whose visibility follows ANOTHER feature key rather than their own tab
// id (the iOS AppFeature.gatingKey equivalent). Both entries ride the orderchat
// grant:
//   • letters        — /api/provider-letters (server/app.js:444);
//   • questhomebound — /api/quest-homebound (:429) and /api/lab-orders (:438).
//
// This alias is only HALF of what those routers demand. Each is
// requireRole('admin','provider') + requirePermission('orderchat'): the alias
// mirrors the grant, and the `role: 'provider'` tag on the same two TABS
// entries mirrors the tier. Both halves are needed — orderchat is grantable to
// a staff-tier role, so the alias alone would hand a front-office user two tabs
// whose every call 403s.
//
// questhomebound was missing here until enforcement was switched on in prod and
// the Lab Orders tab vanished for every non-admin: its tab id is not a feature
// catalog key, so the filter below could never match it and no role could grant
// it back. The alias — not a new catalog key — is the fix, because one
// capability should not answer to two names.
export const TAB_FEATURE_ALIAS = Object.freeze({
  letters: 'orderchat',
  questhomebound: 'orderchat',
});

const TAB_IDS = new Set(DEFAULT_TAB_ORDER);
const TAB_BY_ID = new Map(TABS.map(t => [t.v, t]));

export function isKnownTab(tabId) {
  return TAB_IDS.has(tabId);
}

export function normalizeToolbarOrder(order) {
  const input = Array.isArray(order) ? order : [];
  const seen = new Set();
  const normalized = [];

  for (const tabId of input) {
    if (!TAB_IDS.has(tabId) || seen.has(tabId)) continue;
    seen.add(tabId);
    normalized.push(tabId);
  }
  for (const tabId of DEFAULT_TAB_ORDER) {
    if (seen.has(tabId)) continue;
    normalized.push(tabId);
  }
  return normalized;
}

export function normalizeHiddenTabs(hiddenTabs, order = DEFAULT_TAB_ORDER) {
  const normalizedOrder = normalizeToolbarOrder(order);
  const hidden = new Set(
    (Array.isArray(hiddenTabs) ? hiddenTabs : []).filter(tabId => TAB_IDS.has(tabId))
  );

  const visible = normalizedOrder.filter(tabId => !hidden.has(tabId));
  if (!visible.length && normalizedOrder.length) {
    hidden.delete(normalizedOrder[0]);
  }

  return normalizedOrder.filter(tabId => hidden.has(tabId));
}

export function getOrderedTabs(order) {
  return normalizeToolbarOrder(order).map(tabId => TAB_BY_ID.get(tabId)).filter(Boolean);
}

export function getVisibleTabs(order, hiddenTabs) {
  const hidden = new Set(normalizeHiddenTabs(hiddenTabs, order));
  return getOrderedTabs(order).filter(t => !hidden.has(t.v));
}

export function firstVisibleTab(order, hiddenTabs) {
  return getVisibleTabs(order, hiddenTabs)[0]?.v || DEFAULT_TAB_ORDER[0];
}

// Filter tabs by role and (when feature-permission enforcement is on) by the
// user's feature set. ctx = { role, features, featurePermissionsEnabled }.
// Backward compatible: a bare role string is still accepted.
export function tabsForRole(tabs, ctx) {
  const c = (ctx && typeof ctx === 'object') ? ctx : { role: ctx };
  const r = String(c.role || '').toLowerCase();
  // A tab's `role` is a MINIMUM tier, not an exact match: admin satisfies every
  // tag. That mirrors the server, where admin is in every requireRole list, and
  // it is what lets a tab be tagged 'provider' without vanishing for admins.
  const roleFiltered = tabs.filter((t) => !t.role || r === 'admin' || t.role === r);
  if (!c.featurePermissionsEnabled || r === 'admin') return roleFiltered;
  const features = Array.isArray(c.features) ? c.features : [];
  // Tab `v` IS the feature key for every gateable tab, except aliased tabs
  // (e.g. letters → orderchat) which follow another grant.
  return roleFiltered.filter((t) => features.includes(TAB_FEATURE_ALIAS[t.v] || t.v));
}

// R2/S16, amended by R7/S34 — would this person open the clinical SPA and find
// nothing in it but the Intake tab? Then they are front-office staff and Zephyr
// (/zephyr) is their site: main.jsx sends them there on EVERY landing in the
// main app, rather than into a twelve-tab shell with eleven tabs missing.
// ctx = { role, features, featurePermissionsEnabled }.
//
// The answer is derived from tabsForRole rather than from a second reading of
// the feature list, so the routing rule and the toolbar cannot disagree.
//
// R7 (2026-09-03) removed the one exception this rule used to carry. An
// explicit `?tab=` deep link won, on the theory that a deep link always wins;
// in practice it was a hole — /?tab=intake left an intake-only clerk sitting in
// the clinical SPA. The owner ruled that this class of user's file is Zephyr
// and only Zephyr, so no URL opts them back into the main app. The query
// string is therefore not an input here at all: the answer is a fact about
// their grants, and the caller passes none.
//
// Two hard NOs remain, each protecting something:
//   • enforcement off — the app shows everyone every tab regardless of
//     `features`, so "their only tab is Intake" is not a fact about this
//     practice, and nobody may be routed on it;
//   • admin — admins bypass grants, and one bounced out of the app they
//     administer would have no way back.
//
// Someone with NO grants is not routed either: they meet an empty shell in the
// SPA, and Zephyr would 403 them on its first call — a worse dead end.
export function shouldAutoRouteToZephyr(ctx) {
  const c = (ctx && typeof ctx === 'object') ? ctx : {};
  if (c.featurePermissionsEnabled !== true) return false;
  if (String(c.role || '').toLowerCase() === 'admin') return false;
  const visible = tabsForRole(TABS, {
    role: c.role,
    features: c.features,
    featurePermissionsEnabled: true,
  }).map((t) => t.v);
  return visible.length >= 1 && visible.every((v) => v === 'intake');
}
